import { FormEvent, useEffect, useState } from 'react';
import type {
  AdminAddonCode,
  AdminCompanyRow,
  AdminModeratorRow,
  AdminCreateCompany,
  AdminCreateCompanyResult,
  AdminMe,
  AdminModuleRequest,
  AdminPlanCode,
  AdminSector,
  AdminTotpSetup,
} from '@kassa/shared';
import { adminApi, AdminApiError } from '../../api/admin';
import { ADDON_LABEL, Packages, SubscriptionEditor } from './Packages';
import { Billing } from './Billing';
import { Outlets } from './Outlets';
import { Invites } from './Invites';
import { Signups } from './Signups';
import { AdminHome } from './AdminHome';
import { AiSettings } from './AiSettings';
import { VatRules } from './VatRules';

// Platforma admini (/admin). Müştəri hesabından tam ayrıdır: ayrıca giriş, ayrıca sessiya (2 saat).
// Dizayn: docs/dizayn/Admin.dc.html. 1-ci hissə: şirkətlər, yeni şirkət, modul sorğuları, Authenticator.

const PLAN_LABEL: Record<AdminPlanCode, string> = { simple: 'Sadə', standard: 'Standart', full: 'Tam', custom: 'Özün qur' };
const SECTOR_LABEL: Record<AdminSector, string> = {
  market: 'Market',
  supermarket: 'Supermarket',
  wholesale: 'Topdan',
  agro: 'Aqro',
  restaurant: 'Restoran',
  clothing: 'Geyim',
  service: 'Xidmət',
};
const STATUS_LABEL: Record<string, string> = { trial: 'Sınaq', active: 'Aktiv', frozen: 'Dondurulub', closed: 'Bağlı', overdue: 'Gecikmə', ended: 'Bitib' };

function errText(err: unknown): string {
  return err instanceof AdminApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

type Section = 'home' | 'companies' | 'outlets' | 'packages' | 'billing' | 'requests' | 'security' | 'moderators' | 'ai' | 'vatRules' | 'signups';

export function AdminApp() {
  const [me, setMe] = useState<AdminMe | null>(null);
  const [checking, setChecking] = useState(true);
  // Süzgəcli obyektlər ünvanı (?q=…, ?debt=1 …) yenidən açılanda həmin bölmə
  const [section, setSection] = useState<Section>(() => (/[?&](q|sector|plan|status|debt|idle|trialEnding|page)=/.test(window.location.search) ? 'outlets' : 'home'));
  const [setupCode, setSetupCode] = useState(readAdminSetup);

  useEffect(() => {
    adminApi
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="page-center muted">Yüklənir…</div>;
  if (!me) return setupCode ? <ModeratorSetup code={setupCode} onDone={() => { setSetupCode(''); }} /> : <AdminLogin onLoggedIn={setMe} />;

  async function logout() {
    await adminApi.logout().catch(() => undefined);
    setMe(null);
  }

  // Moderator: yalnız açıq bölmələr; TOTP qoşulmayıbsa — yalnız Authenticator ekranı (KT-64)
  const isOwner = me.role !== 'moderator';
  const has = (s: string) => isOwner || (me.sections ?? []).includes(s);
  // Qruplaşdırılmış menyu (KT-72): moderator yalnız icazəsi olan bəndləri görür; yoxlama API-də və bazadadır
  const groups: { title: string | null; items: [Section, string, boolean][] }[] = [
    { title: null, items: [['home', 'Ana səhifə', true]] },
    {
      title: 'Obyektlər',
      items: [
        ['companies', 'Müştərilər və abunəliklər', has('companies')],
        ['outlets', 'Obyektlər', has('companies')],
        ['signups', 'Yeni qeydiyyatlar', has('companies')],
        ['requests', 'Modul sorğuları', has('packages')],
      ],
    },
    { title: 'Maliyyə', items: [['billing', 'Maliyyə', has('billing')]] },
    { title: 'Komanda', items: [['moderators', 'Moderatorlar', isOwner]] },
    {
      title: 'Sistem',
      items: [
        ['packages', 'Paketlər', has('packages')],
        ['ai', 'AI', has('packages')],
        ['vatRules', 'ƏDV qaydaları', isOwner],
        ['security', 'Təhlükəsizlik', true],
      ],
    },
  ];
  const nav: [Section, string][] = groups.flatMap((g) => g.items.filter(([, , ok]) => ok).map(([k, l]) => [k, l] as [Section, string]));
  const totpGate = !isOwner && !me.totpEnabled;
  const active: Section = totpGate ? 'security' : (nav.some(([k]) => k === section) ? section : (nav[0][0] as Section));

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">piştaxta</span>
        <span className="tag">PLATFORMA ADMİNİ</span>
        <span className="topbar-spacer" />
        <span className="muted">{me.email}</span>
        <button type="button" className="link-btn" onClick={logout}>
          Çıxış
        </button>
      </header>
      <div className="layout">
        <nav className="side-nav" aria-label="Admin bölmələri">
          {groups.map((g) => {
            const items = g.items.filter(([, , ok]) => ok);
            if (items.length === 0) return null;
            return (
              <div key={g.title ?? 'main'} className="nav-group">
                {g.title && <span className="nav-group-title">{g.title}</span>}
                {items.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className="nav-item"
                    aria-current={active === key ? 'page' : undefined}
                    onClick={() => setSection(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            );
          })}
          <p className="muted small">Bu panel yalnız platforma komandası üçündür.</p>
        </nav>
        <main className={active !== 'security' && active !== 'moderators' && active !== 'requests' ? 'content content-wide' : 'content'}>
          {has('companies') && <VatRegimeNotice />}
          {active === 'companies' && <Invites isOwner={isOwner} />}
          {active === 'companies' && <Companies canBilling={has('billing')} />}
          {active === 'signups' && <Signups isOwner={isOwner} canBilling={has('billing')} />}
          {active === 'packages' && <Packages />}
          {active === 'billing' && <Billing isOwner={isOwner} />}
          {active === 'outlets' && <Outlets isOwner={isOwner} canBilling={has('billing')} />}
          {active === 'home' && (
            <AdminHome
              can={(s) => nav.some(([k]) => k === s)}
              onOpen={(l) => {
                if (l.query) window.history.replaceState(null, '', `${window.location.pathname}?${l.query}`);
                setSection(l.section);
              }}
            />
          )}
          {active === 'ai' && <AiSettings isOwner={isOwner} />}
          {active === 'vatRules' && <VatRules />}
          {active === 'requests' && <ModuleRequests />}
          {active === 'moderators' && <Moderators />}
          {active === 'security' && <Security me={me} onChange={setMe} />}
        </main>
      </div>
    </div>
  );
}

function AdminLogin({ onLoggedIn }: { onLoggedIn: (me: AdminMe) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  // İki addım (KT-77): parol və ya Google → Authenticator kodu → sessiya
  const [step, setStep] = useState<'password' | 'totp'>('password');
  const [error, setError] = useState<string | null>(null);
  // Google ilə admin girişi: düymə yalnız funksiya açıq olanda
  const [googleOn, setGoogleOn] = useState(false);
  useEffect(() => {
    fetch('/api/auth/google/enabled', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((b: { enabled: boolean }) => setGoogleOn(b.enabled))
      .catch(() => setGoogleOn(false));
  }, []);
  // Google geri dönüşünün nəticəsi: ?google=<kod>. «totp» — Google yoxlandı, indi Authenticator kodu
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('google');
    if (!code) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (code === 'totp') {
      setStep('totp');
      return;
    }
    const text: Record<string, string> = {
      access_denied: 'Bu Google hesabına hələ icazə verilməyib.',
      google_no_account: 'Bu e-poçt admin siyahısında deyil.',
      totp_tunnel_required: 'Authenticator qoşulmayıb. Əvvəl onu tunel ilə qoşun.',
    };
    setError(Object.prototype.hasOwnProperty.call(text, code) ? text[code] : 'Google ilə giriş alınmadı.');
  }, []);
  const [busy, setBusy] = useState(false);

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await adminApi.login({ email, password });
      if ('totpRequired' in r) {
        setStep('totp');
      } else {
        onLoggedIn(await adminApi.me());
      }
    } catch (err) {
      setError(errText(err));
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi.loginTotp(totp);
      onLoggedIn(await adminApi.me());
    } catch (err) {
      setError(errText(err));
      setTotp('');
      // Ara addımın vaxtı bitdi və ya blok: yenidən parol / Google
      if (!(err instanceof AdminApiError && err.code === 'totp_invalid')) {
        setStep('password');
        setPassword('');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-center">
      {step === 'password' ? (
        <form className="auth-form card" onSubmit={submitPassword} noValidate>
          <div className="auth-heading">
            <h2>Platforma admini</h2>
            <span className="muted">Yalnız platforma komandası üçün</span>
          </div>
          <label className="field">
            E-poçt
            <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} required />
          </label>
          <label className="field">
            Parol
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} required />
          </label>
          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="btn-primary" disabled={busy || !email || !password}>
            {busy ? 'Yoxlanılır…' : 'Daxil ol'}
          </button>
          {googleOn && (
            <a className="btn-secondary" href="/api/auth/google/admin/start">
              Google ilə daxil ol
            </a>
          )}
        </form>
      ) : (
        <form className="auth-form card" onSubmit={submitTotp} noValidate>
          <div className="auth-heading">
            <h2>Authenticator kodu</h2>
            <span className="muted">Telefondakı Google Authenticator-dan 6 rəqəmli kodu yazın. 5 dəqiqə ərzində.</span>
          </div>
          <label className="field">
            Kod
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
              disabled={busy}
              autoFocus
            />
          </label>
          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="btn-primary" disabled={busy || totp.length !== 6}>
            {busy ? 'Yoxlanılır…' : 'Təsdiqlə'}
          </button>
          <button
            type="button"
            className="link-btn"
            disabled={busy}
            onClick={() => {
              setStep('password');
              setTotp('');
              setError(null);
            }}
          >
            Geri
          </button>
        </form>
      )}
    </div>
  );
}

function Companies({ canBilling }: { canBilling: boolean }) {
  const [rows, setRows] = useState<AdminCompanyRow[] | null>(null);
  const [editSub, setEditSub] = useState<{ tenantId: string; outletId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ name: string; result: AdminCreateCompanyResult } | null>(null);

  function load() {
    adminApi
      .companies()
      .then(setRows)
      .catch((err) => setError(errText(err)));
  }
  useEffect(load, []);

  return (
    <>
      <section className="card card-wide">
        <div className="toolbar">
          <h2>Müştərilər və abunəliklər</h2>
          <span className="topbar-spacer" />
          {!creating && (
            <button type="button" className="btn-primary btn-small" onClick={() => { setCreating(true); setCreated(null); }}>
              + Müştəri əlavə et
            </button>
          )}
        </div>
        {created && <SetupCodeNotice name={created.name} result={created.result} onClose={() => setCreated(null)} />}
        {creating && (
          <NewCompany
            onCancel={() => setCreating(false)}
            onCreated={(name, result) => {
              setCreating(false);
              setCreated({ name, result });
              load();
            }}
          />
        )}
        {error && <p className="bad">{error}</p>}
        {editSub && rows && (() => {
          const c = rows.find((x) => x.tenantId === editSub.tenantId);
          const o = c?.outlets.find((x) => x.id === editSub.outletId);
          return c && o ? (
            <SubscriptionEditor
              tenantId={c.tenantId}
              outlet={o}
              onCancel={() => setEditSub(null)}
              onSaved={() => {
                setEditSub(null);
                load();
              }}
            />
          ) : null;
        })()}
        {!rows && !error && <p className="muted">Yüklənir…</p>}
        {rows && rows.length === 0 && <p className="muted">Hələ şirkət yoxdur.</p>}
        {rows && rows.length > 0 && (
          <div className="table-scroll">
          <table className="plain">
            <thead>
              <tr>
                <th>Şirkət · obyekt</th>
                <th>Sahə</th>
                <th>Paket</th>
                <th>Status</th>
                <th>Sahib</th>
              </tr>
            </thead>
            <tbody>
              {rows.flatMap((c) =>
                (c.outlets.length ? c.outlets : [null]).map((o, i) => (
                  <tr key={`${c.tenantId}-${o?.id ?? i}`}>
                    <td>
                      <strong>{c.name}</strong>
                      {c.voen && <span className="muted mono"> · {c.voen}</span>}
                      <div className="muted">{o?.name ?? '—'}</div>
                    </td>
                    <td>{o ? (SECTOR_LABEL[o.sector as AdminSector] ?? o.sector) : '—'}</td>
                    <td>{o?.plan ? (PLAN_LABEL[o.plan as AdminPlanCode] ?? o.plan) : '—'}</td>
                    <td>
                      {STATUS_LABEL[o?.subStatus ?? c.status] ?? o?.subStatus ?? c.status}
                      {o?.trialUntil && <div className="muted small">{o.trialUntil}-dək</div>}
                      {o && Object.keys(o.addons).length > 0 && (
                        <div className="muted small">
                          {Object.entries(o.addons).map(([k, v]) => `+${v} ${ADDON_LABEL[k as AdminAddonCode] ?? k}`).join(', ')}
                        </div>
                      )}
                      {o && o.discountPct && Number(o.discountPct) > 0 && <div className="muted small">endirim {o.discountPct}%</div>}
                      {o && canBilling && (
                        <button type="button" className="link-btn dark small" onClick={() => setEditSub({ tenantId: c.tenantId, outletId: o.id })}>
                          Abunə
                        </button>
                      )}
                    </td>
                    <td>
                      {c.owner ? (
                        <>
                          {c.owner.name}
                          <div className="muted small">{c.owner.phone ?? c.owner.email}</div>
                          <RecoveryLink accountId={c.owner.accountId} name={c.owner.name} />
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </>
  );
}

function SetupCodeNotice({ name, result, onClose }: { name: string; result: AdminCreateCompanyResult; onClose: () => void }) {
  return (
    <div className="card lock-panel" role="status">
      <p className="ok">«{name}» yaradıldı.</p>
      {result.setupCode ? (
        <>
          <p>
            Sahibin birdəfəlik açar kodu (yalnız indi göstərilir, 24 saat keçərlidir):
          </p>
          <p className="mono" style={{ fontSize: '1.25rem', userSelect: 'all' }}>{result.setupCode}</p>
          <p className="muted small">
            Sahib girişdə «Açar kodum var» ilə öz parolunu qoyur. Kodu yalnız sahibə, təhlükəsiz yolla verin.
          </p>
        </>
      ) : (
        <p className="muted">Sahibin hesabı artıq var — mövcud parolu ilə daxil olur.</p>
      )}
      <button type="button" className="link-btn" onClick={onClose}>
        Bağla
      </button>
    </div>
  );
}

const EMPTY: AdminCreateCompany = {
  name: '',
  voen: '',
  outletName: '',
  sector: 'market',
  plan: 'standard',
  trialDays: 30,
  ownerName: '',
  ownerPhone: '',
  ownerEmail: '',
  vatPayer: false,
};

function NewCompany({ onCancel, onCreated }: { onCancel: () => void; onCreated: (name: string, r: AdminCreateCompanyResult) => void }) {
  const [form, setForm] = useState<AdminCreateCompany>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof AdminCreateCompany>(k: K, v: AdminCreateCompany[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: AdminCreateCompany = {
        ...form,
        voen: form.voen || undefined,
        ownerPhone: form.ownerPhone || undefined,
        ownerEmail: form.ownerEmail || undefined,
      };
      onCreated(form.name, await adminApi.createCompany(body));
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="product-form" onSubmit={submit} noValidate aria-label="Yeni şirkət">
      <h3>Yeni şirkət</h3>
      <label className="field">
        Şirkət adı
        <input value={form.name} onChange={(e) => set('name', e.target.value)} required disabled={busy} />
      </label>
      <label className="field">
        VÖEN (10 rəqəm)
        <input inputMode="numeric" maxLength={10} value={form.voen} onChange={(e) => set('voen', e.target.value.replace(/\D/g, ''))} disabled={busy} />
      </label>
      <label className="check">
        <input type="checkbox" checked={!!form.vatPayer} onChange={(e) => set('vatPayer', e.target.checked)} disabled={busy} /> ƏDV ödəyicisi
      </label>
      <label className="field">
        Obyekt adı
        <input value={form.outletName} onChange={(e) => set('outletName', e.target.value)} required disabled={busy} />
      </label>
      <label className="field">
        Sahə
        <select value={form.sector} onChange={(e) => set('sector', e.target.value as AdminSector)} disabled={busy}>
          {(Object.keys(SECTOR_LABEL) as AdminSector[]).map((k) => (
            <option key={k} value={k}>{SECTOR_LABEL[k]}</option>
          ))}
        </select>
      </label>
      <label className="field">
        Forma
        <select value={form.plan} onChange={(e) => set('plan', e.target.value as AdminPlanCode)} disabled={busy}>
          {(Object.keys(PLAN_LABEL) as AdminPlanCode[]).map((k) => (
            <option key={k} value={k}>{PLAN_LABEL[k]}</option>
          ))}
        </select>
      </label>
      <label className="field">
        Sınaq, gün
        <input type="number" min={1} max={90} value={form.trialDays} onChange={(e) => set('trialDays', Number(e.target.value))} disabled={busy} />
      </label>
      <label className="field">
        Sahibin adı
        <input value={form.ownerName} onChange={(e) => set('ownerName', e.target.value)} required disabled={busy} />
      </label>
      <label className="field">
        Sahibin telefonu
        <input inputMode="tel" placeholder="+994 50 000 00 00" value={form.ownerPhone} onChange={(e) => set('ownerPhone', e.target.value)} disabled={busy} />
      </label>
      <label className="field">
        Sahibin e-poçtu
        <input type="email" value={form.ownerEmail} onChange={(e) => set('ownerEmail', e.target.value)} disabled={busy} />
      </label>
      <p className="muted small">Yaradılır: şirkət, hüquqi şəxs, obyekt, «Əsas anbar», «Kassa 1», sınaq abunəsi, sahib hesabı və rolu.</p>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={busy || !form.name || !form.outletName || !form.ownerName || (!form.ownerPhone && !form.ownerEmail)}>
          {busy ? 'Yaradılır…' : 'Yarat'}
        </button>
        <button type="button" className="link-btn" onClick={onCancel} disabled={busy}>
          Ləğv et
        </button>
      </div>
    </form>
  );
}

function ModuleRequests() {
  const [status, setStatus] = useState<'new' | 'all'>('new');
  const [rows, setRows] = useState<AdminModuleRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  function load(s: 'new' | 'all') {
    setRows(null);
    adminApi
      .moduleRequests(s)
      .then(setRows)
      .catch((err) => setError(errText(err)));
  }
  useEffect(() => load(status), [status]);

  async function decide(r: AdminModuleRequest, decision: 'done' | 'rejected') {
    setBusy(r.id);
    setError(null);
    try {
      await adminApi.decide(r.tenantId, r.id, decision);
      load(status);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card card-wide">
      <div className="toolbar">
        <h2>Modul sorğuları</h2>
        <span className="topbar-spacer" />
        <select value={status} onChange={(e) => setStatus(e.target.value as 'new' | 'all')} aria-label="Süzgəc">
          <option value="new">Yeni</option>
          <option value="all">Hamısı</option>
        </select>
      </div>
      {error && <p className="bad">{error}</p>}
      {!rows && !error && <p className="muted">Yüklənir…</p>}
      {rows && rows.length === 0 && <p className="muted">Sorğu yoxdur.</p>}
      {rows && rows.length > 0 && (
        <table className="plain">
          <thead>
            <tr>
              <th>Şirkət · obyekt</th>
              <th>Modul</th>
              <th>Tarix</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <strong>{r.tenantName}</strong>
                  <div className="muted">{r.outletName}</div>
                </td>
                <td>{r.moduleName}</td>
                <td className="mono">{new Date(r.requestedAt).toLocaleString('az-AZ')}</td>
                <td>
                  {r.status === 'new' ? (
                    <>
                      <button type="button" className="btn-primary btn-small" disabled={busy === r.id} onClick={() => decide(r, 'done')}>
                        Açıldı
                      </button>{' '}
                      <button type="button" className="link-btn" disabled={busy === r.id} onClick={() => decide(r, 'rejected')}>
                        Rədd
                      </button>
                    </>
                  ) : (
                    <span className={r.status === 'done' ? 'ok' : 'muted'}>{r.status === 'done' ? 'Açıldı' : 'Rədd'}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Security({ me, onChange }: { me: AdminMe; onChange: (me: AdminMe) => void }) {
  const [setup, setSetup] = useState<AdminTotpSetup | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
      setCode('');
    }
  }

  return (
    <section className="card">
      <h2>Google Authenticator</h2>
      <p className="muted">
        İstəyə görədir. Qoşulandan sonra hər girişdə parol ilə yanaşı 6 rəqəmli kod soruşulur.
      </p>
      {me.totpEnabled ? (
        <>
          <p className="ok">Qoşulub.</p>
          <label className="field">
            Söndürmək üçün cari kod
            <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} disabled={busy} />
          </label>
          <button type="button" className="link-btn" disabled={busy || code.length !== 6} onClick={() => run(async () => onChange(await adminApi.totpDisable(code)))}>
            Söndür
          </button>
        </>
      ) : setup ? (
        <>
          <p>Authenticator proqramında QR kodu skan edin və ya açarı əl ilə daxil edin:</p>
          <img src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qrSvg)}`} alt="Authenticator QR kodu" width={200} height={200} />
          <p className="mono" style={{ userSelect: 'all' }}>{setup.secret}</p>
          <label className="field">
            Proqramdakı kod
            <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} disabled={busy} autoFocus />
          </label>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || code.length !== 6}
            onClick={() =>
              run(async () => {
                onChange(await adminApi.totpEnable(code));
                setSetup(null);
              })
            }
          >
            Təsdiqlə və qoş
          </button>
        </>
      ) : (
        <button type="button" className="btn-primary" disabled={busy} onClick={() => run(async () => setSetup(await adminApi.totpSetup()))}>
          Qoş
        </button>
      )}
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

// Sahib parolu unudanda: admin onu telefonla yoxlayır, 24 saatlıq birdəfəlik link verir (KT-63). Link yalnız bir dəfə göstərilir.
function RecoveryLink({ accountId, name }: { accountId: string; name: string }) {
  const [open, setOpen] = useState(false);
  const [verified, setVerified] = useState(false);
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      setLink(await adminApi.recoveryLink(accountId, verified));
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="link-btn" onClick={() => setOpen(true)}>
        Bərpa linki
      </button>
    );
  }
  return (
    <div className="card lock-panel" role="group" aria-label={`${name} üçün bərpa linki`}>
      {!link ? (
        <>
          <label className="check">
            <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} disabled={busy} /> Sahibi telefonla yoxladım
          </label>
          <p className="muted small">Link 24 saat keçərlidir, bir dəfə işləyir; işlənəndə köhnə girişlər bağlanır.</p>
          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}
          <button type="button" className="btn-primary btn-small" disabled={busy || !verified} onClick={create}>
            Link yarat
          </button>{' '}
          <button type="button" className="link-btn" onClick={() => setOpen(false)} disabled={busy}>
            Ləğv et
          </button>
        </>
      ) : (
        <>
          <p className="ok">Link yaradıldı (yalnız indi göstərilir, {new Date(link.expiresAt).toLocaleString('az-AZ')}-dək):</p>
          <p className="mono" style={{ userSelect: 'all', wordBreak: 'break-all' }}>{link.url}</p>
          <button type="button" className="link-btn" onClick={() => { setLink(null); setOpen(false); setVerified(false); }}>
            Gizlət
          </button>
        </>
      )}
    </div>
  );
}

// Moderatorlar (KT-64): yalnız owner. Link yalnız yaradılan anda bir dəfə göstərilir.
function Moderators() {
  const [rows, setRows] = useState<AdminModeratorRow[] | null>(null);
  const [companies, setCompanies] = useState<AdminCompanyRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ email: string; url: string; expiresAt: string } | null>(null);
  const [form, setForm] = useState({ email: '', name: '', sections: [] as string[], tenantIds: [] as string[] });
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.moderators().then(setRows).catch((err) => setError(errText(err)));
  useEffect(() => {
    load();
    adminApi.companies().then(setCompanies).catch(() => undefined);
  }, []);

  const SECTIONS: [string, string][] = [
    ['companies', 'Müştərilər'],
    ['packages', 'Paketlər'],
    ['billing', 'Ödənişlər'],
    ['support', 'Dəstək (bərpa linki)'],
    ['reports', 'Hesabatlar'],
  ];
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await adminApi.createModerator(form);
      setIssued({ email: form.email, url: r.setupUrl, expiresAt: r.expiresAt });
      setForm({ email: '', name: '', sections: [], tenantIds: [] });
      await load();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  async function setActive(r: AdminModeratorRow, active: boolean) {
    setBusy(true);
    setError(null);
    try {
      await adminApi.updateModerator(r.id, { active });
      await load();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card card-wide">
      <h2>Moderatorlar</h2>
      <p className="muted small">Moderator yalnız açıq bölmələri və siyahıdakı şirkətləri görür. Authenticator məcburidir.</p>
      {issued && (
        <div className="card lock-panel" role="status">
          <p className="ok">{issued.email} üçün quraşdırma linki (24 saat, bir dəfə):</p>
          <p className="mono" style={{ userSelect: 'all', wordBreak: 'break-all' }}>{issued.url}</p>
          <button type="button" className="link-btn" onClick={() => setIssued(null)}>Gizlət</button>
        </div>
      )}
      <form className="product-form" onSubmit={create} noValidate aria-label="Yeni moderator">
        <h3>Yeni moderator</h3>
        <label className="field">Ad<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={busy} /></label>
        <label className="field">E-poçt<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={busy} /></label>
        <fieldset className="choice-list">
          <legend>Bölmələr</legend>
          {SECTIONS.map(([k, l]) => (
            <label key={k} className="check">
              <input type="checkbox" checked={form.sections.includes(k)} onChange={() => setForm({ ...form, sections: toggle(form.sections, k) })} disabled={busy} /> {l}
            </label>
          ))}
        </fieldset>
        <fieldset className="choice-list">
          <legend>Şirkətlər (siyahı)</legend>
          {companies.map((c) => (
            <label key={c.tenantId} className="check">
              <input type="checkbox" checked={form.tenantIds.includes(c.tenantId)} onChange={() => setForm({ ...form, tenantIds: toggle(form.tenantIds, c.tenantId) })} disabled={busy} /> {c.name}
            </label>
          ))}
        </fieldset>
        <button type="submit" className="btn-primary" disabled={busy || !form.email || !form.name || form.sections.length === 0}>
          {busy ? 'Yaradılır…' : 'Moderator yarat'}
        </button>
      </form>
      {error && <p className="bad" role="alert">{error}</p>}
      {!rows && !error && <p className="muted">Yüklənir…</p>}
      {rows && rows.length === 0 && <p className="muted">Moderator yoxdur.</p>}
      {rows && rows.length > 0 && (
        <table className="plain">
          <thead><tr><th>Ad</th><th>Bölmələr</th><th>Şirkət</th><th>TOTP</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.name}</strong><div className="muted small">{r.email}</div></td>
                <td>{r.sections.join(', ')}</td>
                <td>{r.tenantIds.length}</td>
                <td>{r.totpEnabled ? 'qoşulub' : 'yox'}</td>
                <td>
                  <button type="button" className="link-btn" disabled={busy} onClick={() => setActive(r, !r.active)}>
                    {r.active ? 'Deaktiv et' : 'Aktiv et'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Moderator linki: /admin#setup=KOD. Kod fraqmentdədir; oxunandan sonra ünvandan silinir.
function readAdminSetup(): string {
  const m = /^#setup=([A-Za-z0-9-]{16,40})$/.exec(window.location.hash);
  if (!m) return '';
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return m[1];
}

// Moderator parolu qurur (TOTP sonra, girişdən sonra məcburidir).
function ModeratorSetup({ code, onDone }: { code: string; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== repeat) {
      setError('Parollar eyni deyil');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminApi.setupModerator(code, password);
      setDone(true);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-center">
      <form className="auth-form card" onSubmit={submit} noValidate>
        <div className="auth-heading">
          <h2>Moderator — parol</h2>
          <span className="muted">Parolu özünüz qurun. Sonra Authenticator qoşacaqsınız.</span>
        </div>
        {done ? (
          <>
            <p className="ok">Parol quruldu. İndi e-poçt və parolla daxil olun.</p>
            <button type="button" className="btn-primary" onClick={onDone}>Girişə keç</button>
          </>
        ) : (
          <>
            <label className="field">Yeni parol (ən azı 8)<input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} /></label>
            <label className="field">Təkrar<input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} disabled={busy} /></label>
            {error && <p className="bad" role="alert">{error}</p>}
            <button type="submit" className="btn-primary" disabled={busy || password.length < 8 || !repeat}>{busy ? 'Yadda saxlanılır…' : 'Parolu qur'}</button>
          </>
        )}
      </form>
    </div>
  );
}

/** ƏDV rejiminin bitməsi (KT-66): bitməyə ≤ 30 gün qalıb və ya bitib. Mallar avtomatik dəyişmir — sahiblər təsdiqləyir. */
function VatRegimeNotice() {
  const [items, setItems] = useState<{ code: string; endsOn: string | null; active: boolean; expiresSoon: boolean; name: string }[]>([]);
  useEffect(() => {
    adminApi.vatRegimes().then((r) => setItems(r.filter((x) => x.expiresSoon || !x.active))).catch(() => setItems([]));
  }, []);
  if (items.length === 0) return null;
  return (
    <section className="card vat-notice" role="status">
      {items.map((r) => (
        <p key={r.code} className={r.active ? 'small vat-warn' : 'small vat-over'}>
          ƏDV rejimi «{r.name}» {r.endsOn}-də {r.active ? 'bitir' : 'bitib'}. Qanun uzadılmasa, şirkətlər malları 18%-ə keçirməlidir (avtomatik dəyişmir).
        </p>
      ))}
    </section>
  );
}
